import * as Phaser from 'phaser';
import { Prism } from '../entities/Prism';
import { markHowToPlayColorSeen } from '../progress';
import type { LevelSpec } from '../level/schema';
import { LanguageDropdown } from './LanguageDropdown';
import { bindText, onLangChange } from '../i18n/helpers';
import { isRTL } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { FONT, TYPE } from '../render/palette';
import { addAtmosphere } from '../render/atmosphere';
import { buildSwitchPair } from './HowToPlayScene';
import { responsiveFontSize, ensureTouchHitArea } from './responsive-font';

const ICON = 48;

interface HowToPlayColorInitData {
  /** If set, the "Continue" button routes back to the puzzle scene with this level. */
  nextLevel?: LevelSpec;
}

export class HowToPlayColorScene extends Phaser.Scene {
  private nextLevel: LevelSpec | undefined;
  private st!: {
    TITLE: Phaser.Types.GameObjects.Text.TextStyle;
    HEADING: Phaser.Types.GameObjects.Text.TextStyle;
    BODY: Phaser.Types.GameObjects.Text.TextStyle;
    HINT: Phaser.Types.GameObjects.Text.TextStyle;
    LABEL: Phaser.Types.GameObjects.Text.TextStyle;
  };

  constructor() {
    super('how-to-play-color');
  }

  init(data: HowToPlayColorInitData): void {
    this.nextLevel = data.nextLevel;
  }

  create(): void {
    addAtmosphere(this, 0.7);

    this.st = {
      TITLE: {
        color: FONT.TITLE,
        fontFamily: TYPE.SANS,
        fontSize: `${responsiveFontSize(this, 26)}px`,
        fontStyle: '400',
      },
      HEADING: {
        color: FONT.TITLE,
        fontFamily: TYPE.SANS,
        fontSize: `${responsiveFontSize(this, 16)}px`,
        fontStyle: '500',
      },
      BODY: {
        color: FONT.TEXT,
        fontFamily: TYPE.SANS,
        fontSize: `${responsiveFontSize(this, 15)}px`,
      },
      HINT: {
        color: FONT.DIM,
        fontFamily: TYPE.SANS,
        fontSize: `${responsiveFontSize(this, 12)}px`,
      },
      LABEL: {
        color: FONT.DIM,
        fontFamily: TYPE.MONO,
        fontSize: `${responsiveFontSize(this, 11)}px`,
      },
    };

    const titleText = this.add.text(480, 50, '', this.st.TITLE).setOrigin(0.5);
    titleText.setLetterSpacing(8);
    bindText(titleText, 'howtoColor.title', this);

    const pageText = this.add.text(480, 86, '', this.st.HINT).setOrigin(0.5);
    pageText.setLetterSpacing(3);
    bindText(pageText, 'howto.page', this, { n: 2, total: 2 });

    const piecesHeading = this.add.text(480, 130, '', this.st.HEADING).setOrigin(0.5);
    piecesHeading.setLetterSpacing(5);
    bindText(piecesHeading, 'howtoColor.pieces.heading', this);
    this.legend(200);

    const howHeading = this.add.text(480, 320, '', this.st.HEADING).setOrigin(0.5);
    howHeading.setLetterSpacing(5);
    bindText(howHeading, 'howtoColor.how.heading', this);
    bindText(
      this.add.text(480, 360, '', this.st.BODY).setOrigin(0.5),
      'howtoColor.how.line1',
      this,
    );
    bindText(
      this.add.text(480, 384, '', this.st.BODY).setOrigin(0.5),
      'howtoColor.how.line2',
      this,
    );
    bindText(
      this.add.text(480, 408, '', this.st.BODY).setOrigin(0.5),
      'howtoColor.how.line3',
      this,
    );

    bindText(
      this.add.text(480, 460, '', this.st.BODY).setOrigin(0.5),
      'howtoColor.switches.line1',
      this,
    );
    bindText(
      this.add.text(480, 484, '', this.st.BODY).setOrigin(0.5),
      'howtoColor.switches.line2',
      this,
    );
    bindText(
      this.add.text(480, 508, '', this.st.BODY).setOrigin(0.5),
      'howtoColor.switches.line3',
      this,
    );

    const fsNav = responsiveFontSize(this, 13);
    const navStyle = {
      color: FONT.DIM,
      fontFamily: TYPE.SANS,
      fontSize: `${fsNav}px`,
      fontStyle: '500',
    } as const;

    const prev = this.add
      .text(0, 660, '', navStyle)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    ensureTouchHitArea(prev, this, Phaser.Geom.Rectangle);
    prev.setLetterSpacing(3);
    prev.on('pointerover', () => prev.setColor(FONT.TITLE));
    prev.on('pointerout', () => prev.setColor(FONT.DIM));
    prev.on('pointerdown', () => this.scene.start('how-to-play'));
    bindText(prev, 'nav.previousPage', this);

    const rightKey: TranslationKey = this.nextLevel ? 'nav.continueToLevel' : 'nav.backToMenu';
    const continueBtn = this.add
      .text(0, 660, '', navStyle)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    ensureTouchHitArea(continueBtn, this, Phaser.Geom.Rectangle);
    continueBtn.setLetterSpacing(3);
    continueBtn.on('pointerover', () => continueBtn.setColor(FONT.TITLE));
    continueBtn.on('pointerout', () => continueBtn.setColor(FONT.DIM));
    continueBtn.on('pointerdown', () => {
      markHowToPlayColorSeen();
      if (this.nextLevel) this.scene.start('puzzle', { level: this.nextLevel });
      else this.scene.start('menu');
    });
    bindText(continueBtn, rightKey, this);

    const placeChrome = (): void => {
      if (isRTL()) {
        prev.x = 820;
        continueBtn.x = 140;
      } else {
        prev.x = 140;
        continueBtn.x = 820;
      }
    };
    placeChrome();
    onLangChange(this, placeChrome);

    new LanguageDropdown(this);
  }

  private legend(y: number): void {
    const items: Array<[TranslationKey, () => Phaser.GameObjects.GameObject]> = [
      [
        'howtoColor.pieces.prism',
        () => new Prism({ scene: this, cellSize: ICON, gridX: 0, gridY: 0 }),
      ],
      ['howtoColor.pieces.redSwitch', () => buildSwitchPair(this, ICON, ['R'])],
      ['howtoColor.pieces.yellowSwitch', () => buildSwitchPair(this, ICON, ['R', 'G'])],
      ['howtoColor.pieces.whiteSwitch', () => buildSwitchPair(this, ICON, ['R', 'G', 'B'])],
    ];
    const xs = [220, 380, 540, 700];
    items.forEach(([key, make], i) => {
      const obj = make() as unknown as { setPosition(x: number, y: number): void };
      obj.setPosition(xs[i]!, y);
      const label = this.add.text(xs[i]!, y + 44, '', this.st.LABEL).setOrigin(0.5);
      label.setLetterSpacing(2);
      bindText(label, key, this);
    });
  }
}
